import moment from 'moment';
import { t } from 'i18next';
import {
  getUserData, signIn as signInService,
  mfaAuthentication as mfaAuthenticationService
} from '../services/userservice';
import { getContractData as getContractService } from '../services/contractservice';
import { AYLA_ALLOWED_ROLES, ALL_ALLOWED_ROLES } from '../constants';

// ==============================
// ACTION CONSTANTS
// ==============================

const type = {
  ADD_DESCRIPTION: 'ADD_DESCRIPTION',
  ADD_REPORTS: 'ADD_REPORTS',
  DECREMENT_FETCH: 'DECREMENT_FETCH',
  EXPAND_ADD_FILTER: 'EXPAND_ADD_FILTER',
  FETCH_FILTERS: 'FETCH_FILTERS',
  FETCH_REPORTS: 'FETCH_REPORTS',
  FILTER_UPDATED: 'FILTER_UPDATED',
  METERING_API: 'METERING_API',
  HIDE_ERRORS: 'HIDE_ERRORS',
  INCREMENT_FETCH: 'INCREMENT_FETCH',
  INITIALIZE_REPORT: 'INITIALIZE_REPORT',
  LOG_IN: 'LOG_IN',
  LOG_OUT: 'LOG_OUT',
  RESET_FETCH: 'RESET_FETCH',
  SET_MFA_AUTH_CODE: 'SET_MFA_AUTH_CODE',
  SHOW_ERROR: 'SHOW_ERROR',
  SHOW_LOGIN_ERROR: 'SHOW_LOGIN_ERROR',
  TOGGLE_DESCRIPTION: 'TOGGLE_DESCRIPTION',
  TOGGLE_LOADING: 'TOGGLE_LOADING',
  TOGGLE_SIDEBAR: 'TOGGLE_SIDEBAR',
  UPDATE_CONTRACT: 'UPDATE_CONTRACT',
  UPDATE_FILTER: 'UPDATE_FILTER',
};

// ==============================
// ACTION FUNCTIONS
// ==============================

export const showError = error => ({
  error,
  type: type.SHOW_ERROR,
});

export const fetchingReportsData = payload => ({
  payload,
  type: type.FETCH_REPORTS,
});

export const initializeReport = payload => ({
  payload,
  type: type.INITIALIZE_REPORT,
});

export const addReports = payload => ({
  payload,
  type: type.ADD_REPORTS,
});

export const addDescription = payload => ({
  payload,
  type: type.ADD_DESCRIPTION,
});

export const updateContract = payload => ({
  payload,
  type: type.UPDATE_CONTRACT,
});

export const updateFilter = payload => ({
  payload,
  type: type.UPDATE_FILTER,
});

export const toggleDescription = payload => ({
  payload,
  type: type.TOGGLE_DESCRIPTION,
});

export const expandAddFilter = payload => ({
  payload,
  type: type.EXPAND_ADD_FILTER,
});

export const showLoginError = error => ({
  error,
  type: type.SHOW_LOGIN_ERROR,
});

export const incrementFetch = () => ({
  type: type.INCREMENT_FETCH,
});

export const decrementFetch = () => ({
  type: type.DECREMENT_FETCH,
});


export const resetFetch = () => ({
  type: type.RESET_FETCH,
});

export const hideErrors = () => ({
  type: type.HIDE_ERRORS,
});

export const toggleLoading = () => ({
  type: type.TOGGLE_LOADING,
});


export const logIn = session => ({
  session,
  type: type.LOG_IN,
});

export const logOut = () => ({
  type: type.LOG_OUT,
});

export const toggleSidebar = () => ({
  type: type.TOGGLE_SIDEBAR,
});

export const filterUpdated = payload => ({
  payload,
  type: type.FILTER_UPDATED,
});

export const meteringApi = payload => ({
  payload,
  type: type.METERING_API,
});


export const getContractData = () => async (dispatch, getState) => {
  dispatch(incrementFetch());
  try {
    const { presistFilters, session } = getState();
    const contractData = await getContractService(presistFilters.oem_id, session.access_token);
    dispatch(updateContract(contractData));
  } catch (error) {
    dispatch(showError(error));
  } finally {
    dispatch(decrementFetch());
  }
};

export const setMfaAuthCode = authenticationCode => ({
  authenticationCode,
  type: type.SET_MFA_AUTH_CODE,
});

const handleSession = (response) => async (dispatch) => {
  const session = {
    ...response,
    expiration: moment().add(response.expires_in, 'seconds').format()
  };

  const info = await getUserData(session.access_token);
  if (! info.oem) {
    if (! info.origin_oem_str || ! info.role || ! ALL_ALLOWED_ROLES.includes(info.role)) {
      dispatch(showLoginError(t('oemError')));
      return;
    }
  }
  const loggedIn_oem_id = info && info.oem ? info.oem.oem_id : info.origin_oem_str;

  const premium_reports_access = () => {
    if (AYLA_ALLOWED_ROLES.includes(info.role)) return true;
    if (ALL_ALLOWED_ROLES.includes(info.role)) {
      return info.oem ? info.oem.premium_reports : false;
    }
    return false;
  };
  if (loggedIn_oem_id) {
    dispatch(updateFilter({
      logged_in_oem_id: loggedIn_oem_id,
      oem_id: loggedIn_oem_id
    }));
  } else {
    dispatch(showLoginError(t('oemInfoNotPresent')));
    return;
  }
  dispatch(logIn({
    ...session,
    info,
    premium: premium_reports_access(),
  }));
  dispatch(setMfaAuthCode(null));
};

export const mfaAuthentication = (auth_code, mfa_token) => async (dispatch) => {
  dispatch(toggleLoading());
  try {
    const session = await mfaAuthenticationService(mfa_token, auth_code);
    dispatch(handleSession(session));
  } catch (error) {
    //  Error message to be changed
    dispatch(showLoginError(t('wrongAuthCode')));
  } finally {
    dispatch(toggleLoading());
  }
};

export const signIn = (user, password) => async (dispatch) => {
  dispatch(toggleLoading());
  try {
    const response = await signInService(user, password);
    if (response.is_mfa_required) {
      dispatch(setMfaAuthCode(response.auth_code));
      return;
    }
    dispatch(handleSession(response));
  } catch (response) {
    const { error } = response;
    if (error !== undefined) {
      dispatch(showLoginError(error));
    } else {
      dispatch(showLoginError(t('genericError')));
    }
  } finally {
    dispatch(toggleLoading());
  }
};
